import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { StatusBadge, Badge } from '../components/ui/Badge';
import { useAuth } from '../store/auth';
import { useConfirm } from '../components/ui/ConfirmDialog';
import {
  Plus,
  Copy,
  Trash2,
  Share2,
  Info,
  AlertTriangle,
  CheckCircle,
  HelpCircle,
  Power,
  Zap,
} from 'lucide-react';
import { copyToClipboard, formatDateTime, formatRelativeTime, cn } from '../lib/utils';

// ── Types ──────────────────────────────────────────────────────────────────────

interface TrackerDomain {
  id: number;
  hostname: string;
  ownerUserId: number | null;
  isDefault: boolean;
  isShared: boolean;
  status: 'pending_dns' | 'pending_ssl' | 'active' | 'error' | 'disabled';
  sslStatus: 'pending' | 'active' | 'error';
  sslMethod: 'http' | 'txt' | 'cname';
  cloudflareHostnameId: string | null;
  verificationRecord: string | null;
  healthCheckUrl: string | null;
  lastHealthCheckAt: number | null;
  lastHealthStatus: 'up' | 'down' | 'unknown';
  consecutiveFailures: number;
  createdAt: number;
  disabledAt: number | null;
}

interface VerificationInfo {
  type: string;
  name: string;
  value: string;
  status?: string;
}

interface CreateDomainResponse extends TrackerDomain {
  verification: VerificationInfo;
}

// ── Health Dot ─────────────────────────────────────────────────────────────────

function HealthDot({ domain }: { domain: TrackerDomain }) {
  const { lastHealthStatus, consecutiveFailures, lastHealthCheckAt } = domain;

  if (lastHealthStatus === 'up') {
    return (
      <span
        className="flex items-center gap-1.5"
        title={lastHealthCheckAt ? `Last checked: ${formatRelativeTime(lastHealthCheckAt)}` : 'Unknown'}
      >
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-neon-green opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-neon-green" />
        </span>
        <span className="text-[10px] font-mono text-neon-green">UP</span>
      </span>
    );
  }

  if (lastHealthStatus === 'down') {
    return (
      <span
        className="flex items-center gap-1.5"
        title={lastHealthCheckAt ? `Last checked: ${formatRelativeTime(lastHealthCheckAt)}` : 'Unknown'}
      >
        <span className="inline-flex rounded-full h-2 w-2 bg-neon-red" />
        <span className="text-[10px] font-mono text-neon-red">
          DOWN
          {consecutiveFailures > 0 && (
            <span className="text-text-tertiary"> ({consecutiveFailures}x)</span>
          )}
        </span>
      </span>
    );
  }

  return (
    <span
      className="flex items-center gap-1.5"
      title="Never checked"
    >
      <span className="inline-flex rounded-full h-2 w-2 bg-text-tertiary" />
      <span className="text-[10px] font-mono text-text-tertiary">UNKNOWN</span>
    </span>
  );
}

// ── SSL Badge ──────────────────────────────────────────────────────────────────

function SslBadge({ status }: { status: TrackerDomain['sslStatus'] }) {
  if (status === 'active') {
    return (
      <Badge variant="live" className="text-[10px]">
        SSL OK
      </Badge>
    );
  }
  if (status === 'error') {
    return (
      <Badge variant="error" className="text-[10px]">
        SSL ERR
      </Badge>
    );
  }
  return (
    <Badge variant="pending" className="text-[10px]">
      SSL PENDING
    </Badge>
  );
}

// ── Owner Label ────────────────────────────────────────────────────────────────

function OwnerLabel({ domain, currentUserId }: { domain: TrackerDomain; currentUserId: number }) {
  if (domain.isDefault) {
    return <span className="text-[10px] font-mono text-neon-cyan">system</span>;
  }
  if (domain.ownerUserId === currentUserId) {
    return <span className="text-[10px] font-mono text-neon-green">you</span>;
  }
  if (domain.isShared) {
    return (
      <Badge variant="info" className="text-[10px]">
        shared
      </Badge>
    );
  }
  return (
    <span className="text-[10px] font-mono text-text-tertiary">
      uid:{domain.ownerUserId ?? '—'}
    </span>
  );
}

// ── DNS Setup Panel ────────────────────────────────────────────────────────────

function DnsSetupPanel({
  hostname,
  verification,
  onDone,
}: {
  hostname: string;
  verification: VerificationInfo;
  onDone?: () => void;
}) {
  const [copiedName, setCopiedName] = useState(false);
  const [copiedValue, setCopiedValue] = useState(false);

  async function handleCopy(text: string, which: 'name' | 'value') {
    await copyToClipboard(text);
    if (which === 'name') {
      setCopiedName(true);
      setTimeout(() => setCopiedName(false), 2000);
    } else {
      setCopiedValue(true);
      setTimeout(() => setCopiedValue(false), 2000);
    }
  }

  return (
    <div className="cyber-panel border-neon-yellow/40 bg-neon-yellow/5 p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <AlertTriangle size={14} className="text-neon-yellow flex-shrink-0" />
        <span className="font-display text-sm uppercase tracking-widest text-neon-yellow">
          DNS Setup Required
        </span>
      </div>

      {/* DNS record display */}
      <div className="cyber-panel bg-bg-tertiary/50 p-3 space-y-3">
        <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2">
          <span className="text-[10px] font-display uppercase text-text-secondary w-12">Type</span>
          <code className="font-mono text-xs text-neon-cyan">{verification.type.toUpperCase()}</code>
          <span />
        </div>
        <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2">
          <span className="text-[10px] font-display uppercase text-text-secondary w-12">Name</span>
          <code className="font-mono text-xs text-neon-cyan break-all">{verification.name}</code>
          <button
            onClick={() => handleCopy(verification.name, 'name')}
            className="text-text-tertiary hover:text-neon-cyan transition-colors flex-shrink-0"
            title="Copy name"
          >
            {copiedName ? <CheckCircle size={12} className="text-neon-green" /> : <Copy size={12} />}
          </button>
        </div>
        <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2">
          <span className="text-[10px] font-display uppercase text-text-secondary w-12">Value</span>
          <code className="font-mono text-xs text-neon-magenta break-all">{verification.value}</code>
          <button
            onClick={() => handleCopy(verification.value, 'value')}
            className="text-text-tertiary hover:text-neon-cyan transition-colors flex-shrink-0"
            title="Copy value"
          >
            {copiedValue ? <CheckCircle size={12} className="text-neon-green" /> : <Copy size={12} />}
          </button>
        </div>
      </div>

      {/* Step-by-step instructions */}
      <ol className="space-y-1.5 text-[11px] font-mono text-text-secondary">
        <li className="flex gap-2">
          <span className="text-neon-yellow flex-shrink-0">1.</span>
          <span>Log into your DNS registrar (Cloudflare, Namecheap, GoDaddy, etc.)</span>
        </li>
        <li className="flex gap-2">
          <span className="text-neon-yellow flex-shrink-0">2.</span>
          <span>
            Add a <span className="text-neon-cyan">{verification.type.toUpperCase()}</span> record:{' '}
            <code className="text-neon-cyan">{verification.name}</code>
            {' → '}
            <code className="text-neon-magenta">{verification.value}</code>
          </span>
        </li>
        <li className="flex gap-2">
          <span className="text-neon-yellow flex-shrink-0">3.</span>
          <span>
            Cloudflare will automatically provision SSL once DNS resolves
          </span>
        </li>
        <li className="flex gap-2">
          <span className="text-neon-yellow flex-shrink-0">4.</span>
          <span>
            Status will change:{' '}
            <code className="text-neon-yellow">pending_dns</code>
            {' → '}
            <code className="text-neon-yellow">pending_ssl</code>
            {' → '}
            <code className="text-neon-green">active</code>
            {' '}(usually 1–5 minutes)
          </span>
        </li>
      </ol>

      {/* hostname reminder */}
      <p className="text-[10px] font-mono text-text-tertiary">
        Configuring: <code className="text-neon-cyan">{hostname}</code>
      </p>

      {onDone && (
        <div className="flex justify-end pt-1">
          <Button size="sm" onClick={onDone}>
            Done — Refresh Status
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Add Domain Modal ───────────────────────────────────────────────────────────

const HOSTNAME_REGEX = /^[a-z0-9.-]+$/;

function AddDomainModal({
  open,
  onClose,
  isAdmin,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  isAdmin: boolean;
  onCreated: () => void;
}) {
  const qc = useQueryClient();
  const [hostname, setHostname] = useState('');
  const [isShared, setIsShared] = useState(false);
  const [hostnameError, setHostnameError] = useState('');
  const [created, setCreated] = useState<CreateDomainResponse | null>(null);

  const create = useMutation({
    mutationFn: (data: { hostname: string; healthCheckUrl?: string; isShared?: boolean }) =>
      apiClient.post<CreateDomainResponse>('/api/v1/tracker-domains', data),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['tracker-domains'] });
      setCreated(res);
    },
  });

  function validateHostname(value: string): string {
    if (!value) return 'Hostname is required';
    if (!HOSTNAME_REGEX.test(value)) return 'Only lowercase letters, digits, dots and hyphens allowed';
    if (!value.includes('.')) return 'Must be a valid domain (e.g. track.example.com)';
    return '';
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const err = validateHostname(hostname);
    if (err) {
      setHostnameError(err);
      return;
    }
    create.mutate({
      hostname,
      ...(isAdmin ? { isShared } : {}),
    });
  }

  function handleClose() {
    setHostname('');
    setIsShared(false);
    setHostnameError('');
    setCreated(null);
    onClose();
  }

  function handleDone() {
    handleClose();
    onCreated();
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Add Tracker Domain"
      description="Setup custom domain di Cloudflare Workers, lalu register di sini"
      size="lg"
    >
      {!created ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Hostname"
            value={hostname}
            onChange={(e) => {
              setHostname(e.target.value.toLowerCase());
              if (hostnameError) setHostnameError(validateHostname(e.target.value.toLowerCase()));
            }}
            placeholder="track.rjnetwork.site"
            error={hostnameError}
            hint="Subdomain yang akan route ke Tracker Worker"
            required
          />

          {/* Setup instructions */}
          <div className="border border-neon-cyan/30 bg-neon-cyan/5 rounded p-4 space-y-3">
            <div className="text-[11px] font-display uppercase tracking-wider text-neon-cyan flex items-center gap-2">
              📘 Cara setup di Cloudflare
            </div>
            <div className="text-[11px] font-mono text-text-secondary space-y-3 leading-relaxed">
              <div>
                <strong className="text-neon-yellow">Opsi A — Cloudflare Workers Custom Domain (Recommended):</strong>
                <ol className="ml-4 mt-1 space-y-0.5 list-decimal text-text-tertiary">
                  <li>Buka <a href="https://dash.cloudflare.com" target="_blank" rel="noreferrer" className="text-neon-cyan hover:underline">Cloudflare Dashboard</a> → Workers &amp; Pages → <code className="text-neon-cyan">rj-tracker</code></li>
                  <li>Tab <strong>Settings</strong> → <strong>Domains &amp; Routes</strong> → klik <strong>Add</strong> → Custom Domain</li>
                  <li>Masukkan hostname (mis. <code className="text-neon-yellow">{hostname || 'track.rjnetwork.site'}</code>)</li>
                  <li>Cloudflare auto-create DNS + SSL — selesai dalam ~30 detik</li>
                </ol>
              </div>
              <div>
                <strong className="text-neon-yellow">Opsi B — Manual CNAME (jika domain di registrar lain):</strong>
                <ol className="ml-4 mt-1 space-y-0.5 list-decimal text-text-tertiary">
                  <li>DNS provider Anda → tambah CNAME record:</li>
                  <li className="font-mono">
                    <code className="text-neon-yellow">{hostname || '<hostname>'}</code> →{' '}
                    <code className="text-neon-cyan">rj-tracker.jelitanadea77.workers.dev</code>
                  </li>
                  <li>Tunggu propagasi DNS (1-5 menit)</li>
                </ol>
              </div>
              <div className="border-t border-border-subtle pt-2 text-[10px]">
                💡 Setelah setup di atas, klik <strong className="text-neon-cyan">Register Domain</strong> di bawah untuk register hostname di sistem (agar Tracker Worker accept traffic dari hostname ini).
              </div>
            </div>
          </div>

          {isAdmin && (
            <label className="flex items-center gap-3 cursor-pointer group">
              <div
                onClick={() => setIsShared((v) => !v)}
                className={cn(
                  'relative w-10 h-5 rounded-full border transition-colors cursor-pointer',
                  isShared
                    ? 'bg-neon-cyan/20 border-neon-cyan'
                    : 'bg-bg-tertiary border-border-subtle',
                )}
              >
                <span
                  className={cn(
                    'absolute top-0.5 h-4 w-4 rounded-full transition-transform',
                    isShared
                      ? 'translate-x-5 bg-neon-cyan'
                      : 'translate-x-0.5 bg-text-tertiary',
                  )}
                />
              </div>
              <span className="text-xs text-text-secondary group-hover:text-text-primary transition-colors">
                Shared (semua user bisa pakai domain ini)
              </span>
            </label>
          )}

          {create.isError && (
            <p className="text-xs text-neon-red font-mono">
              {(create.error as Error).message}
            </p>
          )}

          <div className="flex gap-2 justify-end pt-2 border-t border-border-subtle">
            <Button type="button" variant="ghost" size="sm" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" size="sm" loading={create.isPending} icon={<Plus size={14} />}>
              Register Domain
            </Button>
          </div>
        </form>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-neon-green">
            <CheckCircle size={16} />
            <span className="font-mono text-sm">
              Domain <code className="text-neon-cyan">{created.hostname}</code> registered successfully
            </span>
          </div>

          <DnsSetupPanel
            hostname={created.hostname}
            verification={created.verification}
            onDone={handleDone}
          />
        </div>
      )}
    </Modal>
  );
}

// ── DNS View Modal (for existing pending domains) ──────────────────────────────

function DnsViewModal({
  domain,
  onClose,
}: {
  domain: TrackerDomain;
  onClose: () => void;
}) {
  let verification: VerificationInfo | null = null;
  try {
    if (domain.verificationRecord) {
      verification = JSON.parse(domain.verificationRecord) as VerificationInfo;
    }
  } catch {
    /* malformed JSON — show fallback */
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="DNS Setup"
      description={domain.hostname}
      size="lg"
    >
      {verification ? (
        <DnsSetupPanel hostname={domain.hostname} verification={verification} />
      ) : (
        <div className="text-sm font-mono text-text-tertiary text-center py-8">
          No verification record available for this domain.
          <br />
          <span className="text-[11px]">Contact support if DNS is not resolving.</span>
        </div>
      )}
    </Modal>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export function TrackerDomainsPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [addOpen, setAddOpen] = useState(false);
  const [dnsViewDomain, setDnsViewDomain] = useState<TrackerDomain | null>(null);

  const list = useQuery({
    queryKey: ['tracker-domains'],
    queryFn: () => apiClient.get<TrackerDomain[]>('/api/v1/tracker-domains'),
  });

  const toggleShared = useMutation({
    mutationFn: ({ id, isShared }: { id: number; isShared: boolean }) =>
      apiClient.patch(`/api/v1/tracker-domains/${id}`, { isShared }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tracker-domains'] }),
  });

  const disable = useMutation({
    mutationFn: (id: number) => apiClient.delete(`/api/v1/tracker-domains/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tracker-domains'] }),
  });

  const hardDelete = useMutation({
    mutationFn: (id: number) => apiClient.delete(`/api/v1/tracker-domains/${id}?hard=true`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tracker-domains'] }),
  });

  const enable = useMutation({
    mutationFn: (id: number) => apiClient.patch(`/api/v1/tracker-domains/${id}`, { status: 'active' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tracker-domains'] }),
  });

  const verify = useMutation({
    mutationFn: (id: number) =>
      apiClient.post<{ ok: boolean; status: string; detail: string }>(
        `/api/v1/tracker-domains/${id}/verify`,
      ),
    onSuccess: (data, id) => {
      qc.invalidateQueries({ queryKey: ['tracker-domains'] });
      if (data.ok) {
        confirm({
          title: '✅ Verified',
          description: `Domain aktif. Status diperbarui ke active. (${data.detail})`,
          variant: 'info',
          actions: [{ label: 'OK', variant: 'neon', result: 'ok' }],
        });
      } else {
        confirm({
          title: '❌ Verification Failed',
          description: data.detail,
          variant: 'warning',
          actions: [
            { label: 'Mark Active Anyway', variant: 'magenta', result: 'force' },
            { label: 'Close', variant: 'ghost', result: 'cancel' },
          ],
        }).then((r) => {
          if (r === 'force') markActive.mutate(id);
        });
      }
    },
  });

  const markActive = useMutation({
    mutationFn: (id: number) => apiClient.post(`/api/v1/tracker-domains/${id}/mark-active`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tracker-domains'] }),
  });

  const domains = list.data ?? [];
  const customDomains = domains.filter((d) => !d.isDefault);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ── Page Header ── */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-display text-2xl font-bold uppercase tracking-widest">
            Tracker Domains
          </h1>
          <p className="text-sm text-text-secondary font-mono">
            // multi-domain failover · {domains.length} domain{domains.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button icon={<Plus size={14} />} onClick={() => setAddOpen(true)}>
          Add Domain
        </Button>
      </div>

      {/* ── Info Banner ── */}
      <div className="cyber-panel border-neon-cyan/30 bg-neon-cyan/5 p-4 flex gap-3">
        <Info size={16} className="text-neon-cyan flex-shrink-0 mt-0.5" />
        <p className="text-sm text-text-secondary font-mono leading-relaxed">
          Add custom hostnames that route traffic to the same Tracker Worker. When one domain
          fails (expired/blocked), tracking links failover to backup domains automatically —
          ensuring zero-downtime tracking without touching your campaign URLs.
        </p>
      </div>

      {/* ── Table ── */}
      {list.isLoading ? (
        <Card className="flex items-center justify-center py-16">
          <span className="inline-block w-6 h-6 border-2 border-neon-cyan border-t-transparent rounded-full animate-spin" />
          <span className="ml-3 font-mono text-sm text-text-tertiary">loading domains...</span>
        </Card>
      ) : customDomains.length === 0 && !list.isLoading ? (
        /* ── Empty State ── */
        <Card className="flex flex-col items-center justify-center py-16 text-center space-y-4">
          <div className="cyber-panel border-neon-cyan/20 bg-bg-tertiary px-8 py-6 font-mono text-sm text-text-tertiary">
            <span className="text-neon-cyan">$</span> No custom domains yet — add one to enable
            failover
          </div>
          <p className="text-xs text-text-tertiary font-mono max-w-sm">
            Your default domain is always available. Adding extra domains provides automatic failover
            when the primary becomes unreachable.
          </p>
          <Button icon={<Plus size={14} />} onClick={() => setAddOpen(true)}>
            Add Your First Domain
          </Button>
        </Card>
      ) : (
        <Card className="p-0">
          <table className="table-cyber">
            <thead>
              <tr>
                <th>Hostname</th>
                <th>Status</th>
                <th>SSL</th>
                <th>Health</th>
                <th>Owner</th>
                <th>Created</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {domains.map((d) => (
                <tr key={d.id} className={d.status === 'disabled' ? 'opacity-50' : undefined}>
                  {/* Hostname */}
                  <td>
                    <div className="flex items-center gap-2">
                      <code className="font-mono text-sm text-neon-cyan">{d.hostname}</code>
                      {d.isDefault && (
                        <Badge variant="info" className="text-[9px]">
                          DEFAULT
                        </Badge>
                      )}
                      {d.isShared && !d.isDefault && (
                        <Badge variant="info" className="text-[9px]">
                          SHARED
                        </Badge>
                      )}
                    </div>
                    {d.healthCheckUrl && (
                      <div className="text-[10px] text-text-tertiary font-mono mt-0.5 truncate max-w-xs">
                        {d.healthCheckUrl}
                      </div>
                    )}
                  </td>

                  {/* Status */}
                  <td>
                    <StatusBadge status={d.status} />
                  </td>

                  {/* SSL */}
                  <td>
                    <SslBadge status={d.sslStatus} />
                  </td>

                  {/* Health */}
                  <td>
                    <HealthDot domain={d} />
                  </td>

                  {/* Owner */}
                  <td>
                    {user && <OwnerLabel domain={d} currentUserId={user.id} />}
                  </td>

                  {/* Created */}
                  <td className="text-xs text-text-tertiary font-mono">
                    {formatDateTime(d.createdAt)}
                  </td>

                  {/* Actions */}
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {/* DNS Setup button for pending domains */}
                      {(d.status === 'pending_dns' || d.status === 'pending_ssl') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={<HelpCircle size={12} />}
                          onClick={() => setDnsViewDomain(d)}
                          title="View DNS Setup instructions"
                        >
                          DNS
                        </Button>
                      )}

                      {/* Verify button — live HTTP probe — for pending/disabled */}
                      {(d.status === 'pending_dns' || d.status === 'pending_ssl' || d.status === 'error') &&
                        (isAdmin || d.ownerUserId === user?.id) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<Zap size={12} />}
                            onClick={() => verify.mutate(d.id)}
                            loading={verify.isPending && verify.variables === d.id}
                            title="Verify: ping /_health & auto-mark active if alive"
                            className="text-neon-green hover:opacity-70"
                          >
                            Verify
                          </Button>
                        )}

                      {/* Admin: toggle shared */}
                      {isAdmin && !d.isDefault && (
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={<Share2 size={12} />}
                          onClick={() => toggleShared.mutate({ id: d.id, isShared: !d.isShared })}
                          title={d.isShared ? 'Make private' : 'Make shared'}
                          className={d.isShared ? 'text-neon-cyan' : undefined}
                        />
                      )}

                      {/* Re-enable disabled domain (admin only) */}
                      {isAdmin && d.status === 'disabled' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={<Power size={12} />}
                          onClick={() => enable.mutate(d.id)}
                          title="Re-enable domain"
                          className="text-neon-green hover:opacity-70"
                        >
                          Enable
                        </Button>
                      )}

                      {/* Disable (soft — toggle off) — separate from delete */}
                      {!d.isDefault && d.status !== 'disabled' &&
                        (isAdmin || d.ownerUserId === user?.id) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<Power size={12} />}
                            onClick={() => {
                              confirm({
                                title: `Disable ${d.hostname}?`,
                                description: 'Domain stops accepting traffic. Bisa di-enable kembali nanti.',
                                variant: 'warning',
                                actions: [{ label: 'Disable', variant: 'magenta', result: 'go', icon: <Power size={14} /> }],
                              }).then((r) => { if (r === 'go') disable.mutate(d.id); });
                            }}
                            title="Disable (soft)"
                            className="text-neon-yellow hover:opacity-70"
                          />
                        )}

                      {/* Delete (hard — permanent) — admin only, never default */}
                      {isAdmin && !d.isDefault && (
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={<Trash2 size={12} />}
                          onClick={() => {
                            confirm({
                              title: `Delete ${d.hostname}?`,
                              description: 'Permanently removes the domain from the system. This cannot be undone.',
                              variant: 'danger',
                              actions: [{ label: 'Delete', variant: 'danger', result: 'go', icon: <Trash2 size={14} /> }],
                            }).then((r) => { if (r === 'go') hardDelete.mutate(d.id); });
                          }}
                          title="Delete permanently"
                          className="text-neon-red hover:opacity-70"
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* ── Add Domain Modal ── */}
      <AddDomainModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        isAdmin={isAdmin}
        onCreated={() => {
          setAddOpen(false);
          qc.invalidateQueries({ queryKey: ['tracker-domains'] });
        }}
      />

      {/* ── DNS View Modal ── */}
      {dnsViewDomain && (
        <DnsViewModal domain={dnsViewDomain} onClose={() => setDnsViewDomain(null)} />
      )}
    </div>
  );
}
